import { Immutable } from "immer";
import React, { useMemo } from "react";
import { Link } from "tsi-common-react/src/components/Link/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Cms, Context } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { MainMenu } from "../MainMenu";
import { IMenuItem } from "../MainMenu/menu.interface";

import siteNavStyles from "../../styles/composable/site-nav.module.scss";
import styles from "./index.module.scss";

type PromoHeaderBlockProps = Immutable<{
    id?: string;
    type?: "promo";
    index?: number;
    value: Cms | null;
}>;

/**
 * PromoHeaderBlock component renders promotional content in the site header
 * Handles both desktop and mobile variants of promo copy
 */
const PromoHeaderBlock = ({
    value,
}: PromoHeaderBlockProps): React.ReactNode => {
    // Simple fallback for mobile copy
    const mobileCopy = value?.mobile_copy || value?.copy || "";
    if (!value) {
        return null;
    }
    return (
        <section>
            <div
                className={concatClassNames([
                    styles.searchNavPromo,
                    "search-nav__promo",
                ])}
                role="region"
                aria-label="Desktop promotional banner"
            >
                <RichText html={value.copy ?? ""} />
            </div>
            <div
                className={concatClassNames([
                    styles.searchNavPromo,
                    styles.searchNavPromoMobile,
                    "search-nav__promo",
                ])}
                role="region"
                aria-label="Mobile promotional banner"
            >
                <RichText html={mobileCopy} />
            </div>
        </section>
    );
};

type IProps = React.ContextType<typeof Context>["chrome"]["site_header"];

export const SiteHeader = (props: IProps) => {
    const homeURL = usePageURL("home");
    const { promo_settings, top_nav_menu } = props;

    // Performance: Memoize menu items to prevent unnecessary recalculations during re-renders
    const menuItems: IMenuItem[] = useMemo(
        () =>
            Array.isArray(top_nav_menu?.menu?.value)
                ? (top_nav_menu.menu.value as IMenuItem[])
                : [],
        [top_nav_menu?.menu?.value],
    );

    return (
        <>
            {/* Landing page intro overlay mount point - can be evaluated during landing page migration */}
            <div id="intro-promo-holder" />
            <header
                id="site-nav-header"
                className={concatClassNames([
                    styles.siteNavContainer,
                    "l-full-width",
                ])}
                role="banner"
            >
                <nav
                    className={concatClassNames([
                        siteNavStyles.siteNav,
                        "site-nav",
                    ])}
                    role="navigation"
                    aria-label="Main navigation"
                >
                    <div className={styles.siteNavInnerContainer}>
                        <div className="site-nav__wrapper">
                            {/* Accessibility: Skip link for keyboard users */}
                            <a
                                className={styles.siteNavScreenReaderSkip}
                                href="#main-content"
                            >
                                {t`Skip to main content`}
                            </a>

                            {/* Desktop navigation */}
                            <div className={styles.siteNavHolder}>
                                <Link
                                    className={concatClassNames([
                                        siteNavStyles.siteNavLogoLink,
                                        "al-site-nav__logo",
                                    ])}
                                    title={t`Tempurpedic Toppers Home`}
                                    href={homeURL}
                                    aria-label={t`Tempur-Pedic Toppers Home`}
                                >
                                    <SVGIcon
                                        name="tempurToppersLogo"
                                        svgClassName={siteNavStyles.siteNavLogo}
                                        alt={t`Tempur-Pedic Toppers Logo`}
                                    />
                                </Link>
                                <div className={styles.siteNavMainMenu}>
                                    <MainMenu menuItems={menuItems} />
                                </div>
                            </div>

                            {/* Mobile navigation */}
                            <div
                                className={concatClassNames([
                                    siteNavStyles.siteNavContent,
                                    "l-capped-width",
                                ])}
                            >
                                <Link
                                    className={concatClassNames([
                                        siteNavStyles.siteNavLogoLink,
                                        siteNavStyles.siteNavLogoLinkMobile,
                                        "al-site-nav__logo",
                                    ])}
                                    title={t`Tempurpedic Toppers Home`}
                                    href={homeURL}
                                    aria-label={t`Tempur-Pedic Toppers Home`}
                                >
                                    <SVGIcon
                                        name="tempurToppersLogo"
                                        svgClassName={
                                            siteNavStyles.siteNavLogoMobile
                                        }
                                        alt={t`Tempur-Pedic Toppers Logo`}
                                    />
                                </Link>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Promotional header section */}
            {promo_settings?.promo_header && (
                <div
                    className={concatClassNames([
                        styles.promoHeader,
                        "promo-header",
                        "l-full-width",
                    ])}
                    role="complementary"
                >
                    <div className="l-capped-width">
                        <StreamField
                            tagName={React.Fragment}
                            value={promo_settings.promo_header.value}
                            components={{
                                promo: PromoHeaderBlock,
                            }}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
